import React, { useState, useRef } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';

import { post } from '../../../../util/request';
import { platform_base_url } from '../../../../util/config';

import * as Styled from './ConfirmationCodeForm.styled';

const NOT_FOUND = 1001;

const ConfirmationCodeSchema = Yup.object().shape({
  code: Yup.string()
    .matches(/\d{6}/, 'Code must be 6 digits')
    .required('Required')
});

/**
 * A Formik Form to enter a confirmation code
 */
const ConfirmationCodeForm = ({ onComplete, onReject, email }) => {
  const [isFetching, setIsFetching] = useState(false);
  const form = useRef();

  const handleOnResendRequest = async () => {
    post(`${platform_base_url}/v2/identity/forgot-password`, {
      email
    })
      .then(() => {
        form.current?.resetForm({ errors: {}, touched: { code: false } });
      })
      .catch((e) => {
        const error = e?.response?.errors?.[0];
        const { title } = error;

        // don't expose to the UI that a user isn't found
        if (error?.reference_code !== NOT_FOUND) {
          form.current?.setErrors({
            code: title || 'Unable to contact server. Please try again.'
          });
        } else {
          form.current?.resetForm({ errors: {}, touched: { code: false } });
        }
      });
  };

  const renderErrorMsg = (error) => {
    const { title, status } = error;
    const INVALID = 406;

    if (status === INVALID) {
      return (
        <>
          <div>Oops, that code isn’t valid.</div>
          <div>
            <Styled.RequestButton onClick={handleOnResendRequest} type="button">
              Request a new one
            </Styled.RequestButton>{' '}
            or try again.
          </div>
        </>
      );
    }

    return title || 'Unable to contact server. Please try again.';
  };

  const handleSubmit = async ({ code }, { resetForm }) => {
    try {
      setIsFetching(true);
      await post(
        `${platform_base_url}/v2/identity/confirm`,
        {
          reset_password_token: code
        },
        { credentials: 'include' }
      );

      onComplete();
    } catch (e) {
      setIsFetching(false);
      const error = e?.response?.errors?.[0];

      resetForm({
        errors: { code: renderErrorMsg(error) },
        touched: { code: true }
      });
    }
  };

  return (
    <Styled.ConfirmationCodeForm>
      <Styled.Heading>
        We sent a confirmation code to
        <Styled.Emphasis> {email} </Styled.Emphasis>
        <br />
        The code expires shortly, so please enter it soon.
      </Styled.Heading>
      <Formik
        innerRef={form}
        initialValues={{ code: '' }}
        validationSchema={ConfirmationCodeSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <Styled.ConfirmationCodeInput
            id="ConfirmationCodeInput"
            name="code"
            autoComplete="one-time-code"
            autoFocus
          />
          <Styled.Button
            isFull
            type="submit"
            size="large"
            variant="primary"
            isLoading={isFetching}
          >
            Submit
          </Styled.Button>
          <Styled.Button isFull size="medium" variant="link" onClick={onReject}>
            Cancel
          </Styled.Button>
        </Form>
      </Formik>
    </Styled.ConfirmationCodeForm>
  );
};

ConfirmationCodeForm.propTypes = {
  /**
   * email address that the confirmation code was sent to
   */
  email: PropTypes.string.isRequired,
  /**
   * A function that is called upon successful password submission.
   */
  onComplete: PropTypes.func.isRequired,
  /**
   * A function that is called when requesting to leave the form.
   */
  onReject: PropTypes.func.isRequired
};

export default ConfirmationCodeForm;
